import { Component, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { AppComponentBase } from 'src/app/app-component-base';
import {
    ComboboxItemDtoOfLong,
    CatalogosGeneralesServiceProxy,
    EventoResponse,
    PatientsServiceProxy,
    PatientMedicalHistoryGetByIdQuery,
    PatientMedicalHistoryResponse,
    PatientMedicalHistoryCreateCommand
} from '../../../shared/service-proxies/service-proxies';
import { DateTimeService } from '../../../shared/services/datetime.service';
import {
    BusquedaEventosGridComponent
} from '../../_components/consultas/eventos/busquedaeventos-grid.component';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

@Component({
    templateUrl: './editpatientmedicalhistory.component.html'
})
export class EditPatientMedicalHistoryComponent extends AppComponentBase implements OnInit {

    form: FormGroup;

    id?: number = null;
    patient?: number = null;
    birthDate?: Date = null;
    model: PatientMedicalHistoryResponse;

    relationshipList: ComboboxItemDtoOfLong[] = [];

    eventoSeleccionado: EventoResponse;

    blockedDocument = false;

    constructor(
        injector: Injector,
        private formBuilder: FormBuilder,
        private service: PatientsServiceProxy,
        private catalogosGeneralesService: CatalogosGeneralesServiceProxy,
        private modalRef: DynamicDialogRef,
        private modalConfig: DynamicDialogConfig,
        private dateTimeService: DateTimeService
    ) {
        super(injector);
    }

    // convenience getter for easy access to form fields
    get f() {
        return this.form.controls;
    }

    ngOnInit() {
        const self = this;

        self.prepareForm();
        self.getRelationship();

        // Si hay un id, buscar el registro correspondiente
        self.id = self.modalConfig.data.id;
        self.patient = self.modalConfig.data.patient;
        self.birthDate = self.modalConfig.data.birthDate;

        if (self.id) {
            self.getForEdit();
        }
    }

    getForEdit(): void {
        const self = this;

        const req = new PatientMedicalHistoryGetByIdQuery();
        req.id = self.id;

        self.service.getPatientMedicalHistoryById(req)
            .pipe(finalize(() => {
                self.blockedDocument = false;
            }))
            .subscribe(data => {
                self.model = data;

                self.f.eventDate.setValue(self.dateTimeService.getDateToAssignInFormControl(data.eventDate));
                self.f.age.setValue(data.age);
                self.f.eventDesc.setValue(data.eventDesc);
                self.f.eventTypeDesc.setValue(data.eventTypeDesc);
                self.f.catalogTypeDesc.setValue(data.catalogTypeDesc);
                self.f.eventCode.setValue(data.eventCode);
                self.f.eventLargeDesc.setValue(data.eventLargeDesc);
                self.f.observations.setValue(data.observations);
                self.f.isHereditary.setValue(data.isHereditary);
                self.f.relationship.setValue(data.relationship);

                self.f.eventDate.disable();
                self.f.observations.disable();
                self.f.isHereditary.disable();
                self.f.relationship.disable();
            });
    }

    getRelationship(): void {
        const self = this;

        self.catalogosGeneralesService.getParentescosForCombobox()
            .pipe(finalize(() => {
                self.blockedDocument = false;
            }))
            .subscribe(data => {
                self.relationshipList = data;
            });
    }

    buscarEventos(): void {
        const self = this;

        const ref = self.dialogService.open(BusquedaEventosGridComponent, {
            styleClass: 'd-sm d-md-65 d-lg-70 d-xl-50',
            header: self.l('Busquedas.Eventos'),
            showHeader: true,
            dismissableMask: false,
            data: {

            }
        });

        ref.onClose.subscribe((p: EventoResponse) => {
            if (p) {
                self.eventoSeleccionado = p;
                self.f.eventDesc.setValue(p.descripcion);
                self.f.eventTypeDesc.setValue(p.tipoEventoDesc);
                self.f.catalogTypeDesc.setValue(p.tipoCatalogoDesc);
                self.f.eventCode.setValue(p.codigoOficial);
                self.f.eventLargeDesc.setValue(p.descripcionAmpliada);
            }
        });
    }

    save(): void {
        const self = this;

        const createCmd = new PatientMedicalHistoryCreateCommand();

        if (self.f.eventDate.value) {
            createCmd.eventDate = self.dateTimeService.getDateTimeToSaveServer(self.f.eventDate.value);
        }

        createCmd.patient = self.patient;
        createCmd.age = self.f.age.value;

        if (self.eventoSeleccionado) {
            createCmd.event = self.eventoSeleccionado.id;
            createCmd.catalogType = self.eventoSeleccionado.tipoCatalogoId;
        }

        createCmd.observations = self.f.observations.value;
        createCmd.isHereditary = self.f.isHereditary.value;
        createCmd.relationship = self.f.relationship.value;

        self.blockedDocument = true;

        self.service.createPatientMedicalHistory(createCmd)
            .pipe(finalize(() => {
                self.blockedDocument = false;
            }))
            .subscribe(data => {
                self.notify.success(self.l('Catalogos.Patients.MedicalHistory.SuccessfulCreate'), self.l('Success'));

                self.close();
            });
    }

    prepareForm(): void {
        const self = this;

        self.form = self.formBuilder.group({
            eventDate: [null],
            age: [null],
            eventDesc: [null],
            eventTypeDesc: [null],
            catalogTypeDesc: [null],
            eventCode: [null],
            eventLargeDesc: [null],
            observations: [null],
            isHereditary: [false],
            relationship: [null]
        });

        self.f.age.disable();
        self.f.eventDesc.disable();
        self.f.eventTypeDesc.disable();
        self.f.catalogTypeDesc.disable();
        self.f.eventCode.disable();
        self.f.eventLargeDesc.disable();
    }

    changeDate(): void {
        const self = this;

        self.f.age.setValue(null);

        if (!self.f.eventDate.value) {
            return;
        }

        const f1 = self.dateTimeService.getDateToMoment(self.birthDate);
        const f2 = self.dateTimeService.getDateToMoment(self.f.eventDate.value as Date);

        self.f.age.setValue(f2 >= f1 ? f2.diff(f1, 'years') : null);
    }

    close(): void {
        const self = this;
        self.modalRef.close();
    }
}
